import type { User } from 'core/Api';
import { useUserData } from 'core/Api';
import { useEffect } from 'react';
import usePlatformService from './usePlatformService';

interface IGtmService {
  events: {
    logIn: (user: User) => void;
    signUp: (user: User) => void;
    share: () => void;
    cardAdded: () => void;
    extensionInstalled: (extensionType: PlatformExtensions) => void;
  };
  click: (callback: () => void, config: UserClickConfigs) => void;
  action: any;
  log: (data: TrackLog) => void;
  modalView: (modalName: ModalViews) => void;
  updateUser: () => void;
}
interface UserClick {
  callback: () => void;
  config: UserClickConfigs;
}

type UserClickConfigs = UserClickButtonConfig | UserClickLinkConfig;

interface UserClickButtonConfig {
  type: 'button';
  action:
    | 'submit'
    | 'skip'
    | 'info'
    | 'sign_up'
    | 'add_card'
    | 'install_extension'
    | 'download_app';
  label?: string;
}
interface UserClickLinkConfig {
  type: 'link';
  action: string;
  label?: never;
}

interface UserGtmEvent {
  event: 'login' | 'sign_up' | 'click' | 'card_added' | 'extension_installed';
}
interface GtmUserData {
  userId?: string;
  loginMethod?: string;
  platform?: string;
  mobileBrowserType?: boolean | string;
  installed_chrome?: boolean;
  installed_mobile?: boolean;
  installed_android?: boolean;
  installed_ios?: boolean;
  setup_credit_cards?: boolean;
}

interface UserLogInEvent extends UserGtmEvent, GtmUserData {
  event: 'login';
}
interface UserSignUpEvent extends UserGtmEvent, GtmUserData {
  event: 'sign_up';
}
interface UserCardAdd extends UserGtmEvent, GtmUserData {
  event: 'card_added';
}

type SendEvent = UserLogInEvent | UserSignUpEvent | UserCardAdd | any;

type ModalViews = 'add-card' | 'learn-more';

interface TrackAction {
  type: 'merchant_search' | 'cause_search';
  value: string;
}

type PlatformExtensions = 'chrome' | 'android' | 'ios' | 'safari';

type TrackLog =
  | 'chrome_extension_installed'
  | 'chrome_extension_not_installed'
  | 'safari_extension_installed'
  | 'safari_extension_not_installed'
  | 'edge_extension_installed'
  | 'edge_extension_not_installed';

// This is a WIP. I'm still finishing some of the types and the actual implementation.
// If your using this, you should probably not unless you know what you're doing and how GTM works.
export default function useGtmServices() {
  const Platform = usePlatformService();
  const { data: userData } = useUserData((user) => ({
    uuid: user?.uuid,
    authenticated_by: user?.authenticated_by,
    onboarding: user?.onboarding,
  }));

  // GTM Constants
  const user: GtmUserData = {
    userId: userData?.uuid,
    loginMethod: userData?.authenticated_by,
    platform: Platform.OS,
    mobileBrowserType: Platform.mobileBrowser,
    installed_chrome: userData?.onboarding?.installed_chrome,
    installed_android: userData?.onboarding?.installed_android,
    installed_ios: userData?.onboarding?.installed_ios,
    installed_mobile: userData?.onboarding?.installed_mobile,
    setup_credit_cards: userData?.onboarding?.setup_credit_cards,
  };

  const sendEvent = (load: SendEvent) => {
    window.dataLayer.push(load);
  };

  const logIn = (userData?: User) => {
    const login: UserLogInEvent = {
      event: 'login',
      ...user,
      userId: userData?.uuid,
      loginMethod: userData?.authenticated_by,
      ...userData,
    };
    sendEvent(login);
  };

  const cardAdded = () => {
    const cardAdd: UserCardAdd = {
      event: 'card_added',
      ...user,
    };
    sendEvent(cardAdd);
  };

  const extensionInstalled = (extension: PlatformExtensions) => {
    const extensionInstalled: SendEvent = {
      event: 'extension_installed',
      platform: extension,
      ...user,
    };
    sendEvent(extensionInstalled);
  };

  const signUp = (userData?: User) => {
    const signUpEvent: UserSignUpEvent = {
      event: 'sign_up',
      ...user,
      userId: userData?.uuid,
      loginMethod: userData?.authenticated_by,
      ...userData,
    };
    sendEvent(signUpEvent);
  };

  const trackClick = ({ callback, config }: UserClick) => {
    const event = {
      event: 'click',
      type: config.type,
      action: config.action,
      label: config?.label,
      value: config?.label,
    };

    sendEvent(event);
    return callback();
  };

  const trackModalView = (modalName: ModalViews) => {
    const view = {
      event: 'modal_view',
      modalName,
      ...user,
    };
    sendEvent(view);
  };

  const trackAction = ({ type, value }: TrackAction) => {
    const action = {
      event: 'action',
      type: type,
      value: value,
    };
    sendEvent(action);
  };

  const trackLogs = (data: TrackLog) => {
    const action = {
      event: 'dataLog',
      dataLog: data,
      ...user,
    };

    // Send this to set the user property in ga
    if (data === 'chrome_extension_installed') {
      // Object.assign(action, { chromeExtensionInstalled: true });
    }
    if (data === 'chrome_extension_not_installed') {
      // Object.assign(action, { chromeExtensionInstalled: false });
    }

    sendEvent(action);
  };

  const updateUser = () => {
    sendEvent({ event: 'update_user', ...user });
  };

  /**
   *
   * Provides an way to send specific events to GTM.
   *
   * @key events: Allows pre-specified non generic event tracking. Generic event's should be handled elsewhere
   * @key click: Allows click events to be tracked, ideally with a type-value pair
   * @key action: Allows specified user interactions to be tracked. (i.e Searching, Car)
   * @key log: Allows tracking of data we want to log to GTM, but that doesn't represent a direct action
   * @key modalView: Allows tracking of when a modal is opened
   * @key updateUser: Useful for just setting the DLV you want
   */
  const GtmService: IGtmService = {
    events: {
      logIn,
      signUp,
      share: () => console.log('This would share the activity'),
      cardAdded: () => cardAdded(),
      extensionInstalled: extensionInstalled,
    },
    click: (callback, config) => trackClick({ callback, config }),
    action: ({ type, value }: TrackAction) => trackAction({ type, value }),
    log: (data: TrackLog) => trackLogs(data),
    modalView: (modalName: ModalViews) => trackModalView(modalName),
    updateUser: () => updateUser(),
  };

  useEffect(() => {
    // Add dataLayer to window when not present
    window.dataLayer ??= [];
  }, []);

  return GtmService;
}
